import React from "react";
import {
  AlertTriangle,
  Angry,
  Ban,
  Bed,
  CheckCircle,
  CircleSlash2,
  Clock,
  Coffee,
  Flag,
  Flame,
  Frown,
  Gauge,
  Ghost,
  Handshake,
  Heart,
  Hourglass,
  MessageSquareWarning,
  RefreshCw,
  Rocket,
  Scale,
  ScrollText,
  Smile,
  Sparkles,
  Star,
  TrendingDown,
  TrendingUp,
} from "lucide-react";

export type Flag = {
  key: string;
  icon: React.ElementType;
  title: string;
  activeStatus: "success" | "error" | "warning";
};

const FLAGS: Flag[] = [
  {
    key: "shows_love",
    icon: Heart,
    title: "Shows Love",
    activeStatus: "success", // Good thing true
  },
  {
    key: "is_dissatisfied",
    icon: Frown,
    title: "Dissatisfied",
    activeStatus: "error",
  },
  {
    key: "is_ghost",
    icon: Ghost,
    title: "Ghost",
    activeStatus: "error",
  },
  {
    key: "ineffective_service",
    icon: AlertTriangle,
    title: "Ineffective",
    activeStatus: "warning",
  },
  {
    key: "stalled_engagement",
    icon: Hourglass,
    title: "Stalled",
    activeStatus: "warning",
  },
  {
    key: "is_waiting",
    icon: Clock,
    title: "Waiting",
    activeStatus: "error",
  },
  {
    key: "idle_tasks",
    icon: Coffee,
    title: "Idle Tasks",
    activeStatus: "warning",
  },
  {
    key: "is_effective",
    icon: CheckCircle,
    title: "Effective",
    activeStatus: "success",
  },
  {
    key: "good_rapport",
    icon: Handshake,
    title: "Rapport",
    activeStatus: "success",
  },
  {
    key: "awesome_experience_last_24_hours",
    icon: Star,
    title: "Awesome Experience",
    activeStatus: "success",
  },
  {
    key: "explicit_cancel_pause_downgrade_or_refund_intent",
    icon: Ban,
    title: "Cancel/Pause Intent",
    activeStatus: "error",
  },
  {
    key: "low_or_declining_utilization",
    icon: TrendingDown,
    title: "Low Utilization",
    activeStatus: "warning",
  },
  {
    key: "onboarding_at_risk",
    icon: Rocket,
    title: "Onboarding at Risk",
    activeStatus: "error",
  },
  {
    key: "expectation_or_fit_mismatch",
    icon: Scale,
    title: "Expectation/Fit Mismatch",
    activeStatus: "warning",
  },
  {
    key: "rapport_or_communication_risk",
    icon: MessageSquareWarning,
    title: "Communication Risk",
    activeStatus: "warning",
  },
  {
    key: "expected_inactivity",
    icon: Bed,
    title: "Expected Inactivity",
    activeStatus: "success",
  },
  {
    key: "recovery_signal",
    icon: RefreshCw,
    title: "Recovery Signal",
    activeStatus: "success",
  },
  {
    key: "plan_capacity_mismatch",
    icon: Gauge,
    title: "Plan Capacity Mismatch",
    activeStatus: "warning",
  },
  {
    key: "first_14_days_wow",
    icon: Sparkles,
    title: "First 14 Days Wow",
    activeStatus: "success",
  },
];

// Retain definitions for historical annotations without offering removed checks
// in current filters or flag pickers.
const LEGACY_FLAGS: Flag[] = [
  {
    key: "is_active",
    icon: Smile,
    title: "Active",
    activeStatus: "success",
  },
  {
    key: "is_client_frustrated",
    icon: Angry,
    title: "Frustrated",
    activeStatus: "error",
  },
  {
    key: "client_disrespectful",
    icon: Flame,
    title: "Disrespectful",
    activeStatus: "error",
  },
];

const CHECKIN_FLAGS: Flag[] = [
  {
    key: "warning",
    icon: AlertTriangle,
    title: "Warnings",
    activeStatus: "warning",
  },
  {
    key: "red_flag",
    icon: Flag,
    title: "Red Flag",
    activeStatus: "error",
  },
  {
    key: "likely_cancel",
    icon: CircleSlash2,
    title: "Likely Cancel",
    activeStatus: "error",
  },
  {
    key: "redeemer",
    icon: TrendingUp,
    title: "Redeemers",
    activeStatus: "success",
  },
  {
    key: "winning",
    icon: Star,
    title: "Winning",
    activeStatus: "success",
  },
  {
    key: "special_notes",
    icon: ScrollText,
    title: "Special Note",
    activeStatus: "warning",
  },
];

const FLAGS_MAP = [...FLAGS, ...CHECKIN_FLAGS, ...LEGACY_FLAGS].reduce(
  (acc, flag) => {
    acc[flag.key] = flag;
    return acc;
  },
  {} as Record<string, Flag>,
);

export { FLAGS, CHECKIN_FLAGS, FLAGS_MAP };
